// Copyright (c) Microsoft. All rights reserved.
import { AIModelName } from '../app/AIModel';
import { WebSearchSource } from '../app/Plugin';
import { IMCPResponse } from '../app/mcp/Tool';
import { ExternalSource } from '../studio/PluginPromptList';
import { IFileAnalyzedCacheResult } from './File';
import { IApiResult } from '../app/APIResult';

/**
 * Type of the chat message. A copy of ChatMessageType in the API C# code.
 */
export enum ChatMessageType {
  // A message containing text
  ChatMessage = 'ChatMessageV2',

  // A message for a Plan
  // Plan,

  // A message showing an uploaded document
  Document = 'Document',
}

export enum ChatMessageErrorType {
  SystemError = 'SystemError',
  Exception = 'Exception',
  TooLong = 'TooLong',
  LimitRate = 'LimitRate',
}

/**
 * Citation for the response
 */
export interface IChatMessageSearchFact {
  linkIndex?: string;
  sourceName: string;
  relevance: number;
  fact: string;
  factIndex?: string;
  notDownloadable: boolean;
}

export enum ChatMessageRole {
  System = 'System',
  User = 'User',
  Assistant = 'Assistant',
}

export interface IChatMessageList {
  continuationToken: string | null;
  data: IChatMessage[];
}

export interface ChatMessageList {
  continuationToken: string | null;
  data: ChatMessage[];
}

export interface IChatMessage {
  id: string;
  partitionKey?: string;
  chatRoomId: string;
  messages: IChatMessageItem[];
  files?: IFileAnalyzedCacheResult[];
  responseFrom: string | null;
  role: ChatMessageRole;
  type: ChatMessageType;
  updatedTime?: number;
  createdTime?: number;
}

export interface IChatMessageItem {
  itemId: string;
  userId: string;
  userName: string;
  profileUrl: string;
  message: string;
  model: string;
  modelId: string;
  pluginId: string;
  progress?: IChatMessageProgress;
  searchResult?: IChatMessageSearchResult;
  references?: IChatMessageReference[];
  conversationStarters?: string[];
  externalSource?: ExternalSource;
  functionResults?: IChatMessageFunctionResult[];
  errorMessage?: string;
  hasError: boolean;
  errorType?: ChatMessageErrorType;
}

export interface IChatStreamingMessage {
  chatRoomId: string;
  chatId: string | null;
  chatItemId: number;
  message?: string;
  references?: IChatMessageReference[];
  hasError: boolean;
  errorMessage?: string;
  errorDebugMessage?: string;
  searchResult?: IChatMessageSearchResult;
  toolResult?: IMCPResponse;
  functionResult?: IChatMessageFunctionResult;
}



export interface IChatMessageReference {
  thumbnailUri: string;
  title: string;
  subTitle?: string;
  uri: string;
  index: number;
  isSecured: boolean;
  content: string;
  hideReference: boolean;
  author?: string;
  publishedDate?: number;
  videoMetaData?: IChatMessageSearchVideo;
  source: string;
}

export interface IChatMessageProgress {
  currentMessage: string;
  currentStage: number;
  totalStage: number;
  items: IChatMessageProgressItem[];
}

export interface IChatMessageProgressItem {
  message: string;
  isCompleted: boolean;
  hasError: boolean;
  progressName: string;
  references?: IChatMessageReference[];
  dataType: string;
  data: string;
}

export interface IChatStreamingProgressMessage {
  chatId: string;
  chatItemId: number;
  progress: IChatMessageProgress;
  searchResult?: IChatMessageSearchResult;
}

export interface IChatSendMessage {
  ChatRoomId?: string;
  Message: string;
  AIModelName: string;
  PluginId?: string | undefined;
  AttachFiles?: IFileAnalyzedCacheResult[] | undefined;
}
export interface IChatMessageSearchResult {
  results: IChatMessageWebSearchResult[];
  queries: IChatMessageSearchQuery[];
}
export interface IChatMessageWebSearchResult {
  id: string;
  title?: string;
  responseTime?: number;
  references: IChatMessageReference[];
  facts: IChatMessageSearchFact[];
  images: IChatMessageSearchImageReference[];
  sourceName: string;
  sourceType?: WebSearchSource;
  isProgress: boolean;
  errorQuery: string[];
  hasError: boolean;
  queries?: IChatMessageSearchQuery[];
}

export interface IChatMessageSearchFact {
  linkIndex?: string;
  sourceName: string;
  relevance: number;
  fact: string;
  factIndex?: string;
  notDownloadable: boolean;
}

export interface IChatMessageSearchQuery {
  query: string;
  preferenceSource: WebSearchSource;
}

export interface IChatMessageSearchVideo {
  duration: number;
  viewCount: number;
  keywords: string[];
}

export interface IChatMessageSearchImageReference {
  url: string;
  description?: string;
}

export interface IChatMessageFunctionResult {
  pluginName: string;
  functionName: string;
  functionArguments: Record<string, object>;
  result: string;
}


export interface AgentResponseUpdate {
  additionalProperties: Record<string, object>;
  agentId: string;
  authorName: string;
  contents?: AIContent[];
  continuationToken?: string;
  createdAt: number;
  messageId?: string;
  responseId?: string;
  rawRepresentation: object;
  role?: ChatRole;
  text?: string;
}

export interface ChatMessage {
  additionalProperties?: Record<string, object>;
  id: string;
  sessionId?: string;
  messageId?: string;
  author?: ChatMessageAutor;
  contents?: AIContent[];
  attachedFiles?: IFileAnalyzedCacheResult[];
  requestMeta: ChatMessageRequestMeta;
  resultMeta?: ChatMessageResultMeta;
  progress?: IChatMessageProgress;
  preMessages?: ChatMessage[];
  role: ChatRole;
  type: string;
  partitionKey?: string;
}

export interface ChatMessageRequest {
  additionalProperties?: Record<string, object>;
  sessionId?: string;
  messageId?: string;
  message: string;
  attachedFiles?: IFileAnalyzedCacheResult[];
  agentId?: string
  agentMode: boolean;
  cacheMode: boolean;
  webSearchOn: boolean;
  webSearchSource: WebSearchSource;
  model: AIModelName;
}

export interface ChatMessageAutor {
  userId?: string;
  userName?: string;
  profileUrl?: string;
  companycode?: string;
}

export interface ChatMessageRequestMeta {
  model?: AIModelName;
  modelString?: string;
  externalSource?: string;
  agentId?: string;
}

export interface ChatMessageResultMeta {
  hasError: boolean;
  errorType: string;
  errorMessage: string;
}

export interface AIContent {
  rawRepresentation?: object;
  additionalProperties: Record<string, object>;
}

export interface TextContent extends AIContent {
  text: string;
}
export interface SuggestedQuestionsResult {
  result: string;
  data: string[];
}

export interface FunctionResultContent extends AIContent {
  result: IApiResult | IChatMessageWebSearchResult | SuggestedQuestionsResult;
}

export const isSuggestedQuestionsResult = (value: unknown): value is SuggestedQuestionsResult => {
  if (typeof value !== 'object' || value === null || !('data' in value)) {
    return false;
  }
  const data: unknown = (value as { data: unknown }).data;
  return Array.isArray(data) && data.every((d): d is string => typeof d === 'string');
};

export enum ChatRole {
  System = 'system',
  Assistant = 'assistant',
  User = 'user'
}

export const isTextContent = (obj: unknown): obj is TextContent =>
  obj !== null &&
  typeof obj === "object" &&
  "text" in obj &&
  typeof (obj as { text: unknown }).text === "string";

export const isFunctionResultContent = (obj: unknown): obj is FunctionResultContent =>
  obj !== null &&
  typeof obj === "object" &&
  "$type" in obj &&
  (obj as { $type: unknown }).$type == "functionResult";

export const isApiResult = (data: unknown): data is IApiResult => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'result' in data
  );
};

export const SearchResultCheckType = (data: unknown): data is IChatMessageSearchResult => {
  return typeof data == 'object' && data != null && typeof (data as IChatMessageSearchResult).results == 'object';
}
